import React, { useState, useEffect, useOptimistic } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Building2, MapPin, Briefcase, Globe, Trash2 } from 'lucide-react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { getPeopleByUuid, deletePeople, updatePeopleCoordinates } from '../../services/apiService';
import { geocodeAddress } from '../../services/geocodingService';
import logo from '../../assets/logo.png';

/**
 * Normalize people data to handle both lowercase and uppercase keys from ColdFusion
 */
const normalizePeople = (people) => ({
    id: people.id || people.ID,
    peopleUuid: people.peopleUuid || people.PEOPLEUUID,
    firstName: people.firstName || people.FIRSTNAME,
    lastName: people.lastName || people.LASTNAME,
    email: people.email || people.EMAIL,
    phone: people.phone || people.PHONE,
    company: people.company || people.COMPANY,
    position: people.position || people.POSITION,
    address: people.address || people.ADDRESS,
    website: people.website || people.WEBSITE,
    latitude: people.latitude || people.LATITUDE,
    longitude: people.longitude || people.LONGITUDE,
    createdAt: people.createdAt || people.CREATEDAT,
    updatedAt: people.updatedAt || people.UPDATEDAT
});

/**
 * Full page view for people details
 */
const PeopleDetail = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [people, setPeople] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [geocoding, setGeocoding] = useState(false);

    // React 19: useOptimistic for instant UI updates
    const [optimisticPeople, updateOptimisticPeople] = useOptimistic(
        people,
        (state, newData) => state ? { ...state, ...newData } : null
    );

    useEffect(() => {
        const loadPeople = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getPeopleByUuid(uuid);
                const normalizedPeople = normalizePeople(data);
                setPeople(normalizedPeople);

                // If address exists but coordinates don't, geocode the address
                if (normalizedPeople.address && (!normalizedPeople.latitude || !normalizedPeople.longitude)) {
                    geocodePeopleAddress(normalizedPeople);
                }
            } catch (err) {
                console.error('Error loading people:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadPeople();
    }, [uuid]);

    const geocodePeopleAddress = async (peopleData) => {
        setGeocoding(true);

        // React 19: Optimistically update UI immediately
        updateOptimisticPeople({
            latitude: 'loading',
            longitude: 'loading'
        });

        try {
            console.log('Geocoding address for people:', peopleData.address);
            const coordinates = await geocodeAddress(peopleData.address);

            // Update coordinates in database
            await updatePeopleCoordinates(peopleData.peopleUuid, coordinates.latitude, coordinates.longitude);

            // Update actual state (optimistic state will automatically sync)
            setPeople(prev => ({
                ...prev,
                latitude: coordinates.latitude,
                longitude: coordinates.longitude
            }));

            console.log('Coordinates updated successfully');
        } catch (err) {
            console.error('Error geocoding address:', err);
            // useOptimistic will automatically rollback on error
        } finally {
            setGeocoding(false);
        }
    };

    const handleDelete = async () => {
        if (!displayPeople) return;

        if (!window.confirm(`Supprimer ${displayPeople.firstName} ${displayPeople.lastName}?`)) {
            return;
        }

        try {
            await deletePeople(displayPeople.peopleUuid);
            navigate('/');
        } catch (err) {
            console.error('Error deleting people:', err);
            alert(`Erreur lors de la suppression: ${err.message}`);
        }
    };

    // React 19: Use optimisticPeople for display to show instant updates
    const displayPeople = optimisticPeople || people;

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error || !displayPeople) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center max-w-md">
                    <p className="text-red-600 text-lg mb-4">
                        {error || 'Personne non trouvée'}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="text-cabaza-purple hover:text-cabaza-pink transition-colors"
                    >
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* React 19: Document Metadata */}
            {displayPeople && (
                <>
                    <title>{displayPeople.firstName} {displayPeople.lastName} - Cabaza</title>
                    <meta name="description" content={`Contact de ${displayPeople.firstName} ${displayPeople.lastName}${displayPeople.company ? ` - ${displayPeople.company}` : ''}`} />
                </>
            )}

            {/* Header */}
            <div className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center text-cabaza-purple hover:text-cabaza-pink transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Retour aux contacts
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Card */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-8 text-white">
                        <div className="flex items-center mb-4">
                            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mr-6">
                                <User className="w-10 h-10 text-cabaza-purple" />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold mb-2">
                                    {displayPeople.firstName} {displayPeople.lastName}
                                </h1>
                                {people.position && (
                                    <p className="text-xl opacity-90">{displayPeople.position}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Company */}
                            {people.company && (
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-cabaza-purple bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <Building2 className="w-6 h-6 text-cabaza-purple" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium mb-1">Entreprise</p>
                                        <p className="text-lg text-gray-900">{displayPeople.company}</p>
                                    </div>
                                </div>
                            )}

                            {/* Email */}
                            {people.email && (
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-cabaza-purple bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <Mail className="w-6 h-6 text-cabaza-purple" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium mb-1">Email</p>
                                        <a
                                            href={`mailto:${displayPeople.email}`}
                                            className="text-lg text-cabaza-purple hover:text-cabaza-pink transition-colors"
                                        >
                                            {displayPeople.email}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Phone */}
                            {people.phone && (
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-cabaza-purple bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <Phone className="w-6 h-6 text-cabaza-purple" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium mb-1">Téléphone</p>
                                        <a
                                            href={`tel:${displayPeople.phone}`}
                                            className="text-lg text-cabaza-purple hover:text-cabaza-pink transition-colors"
                                        >
                                            {displayPeople.phone}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Website */}
                            {people.website && (
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-cabaza-purple bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <Globe className="w-6 h-6 text-cabaza-purple" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium mb-1">Site web</p>
                                        <a
                                            href={displayPeople.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-lg text-cabaza-purple hover:text-cabaza-pink transition-colors break-all"
                                        >
                                            {displayPeople.website}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Address */}
                            {people.address && (
                                <div className="flex items-start md:col-span-2">
                                    <div className="w-12 h-12 bg-cabaza-purple bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <MapPin className="w-6 h-6 text-cabaza-purple" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium mb-1">Adresse</p>
                                        <p className="text-lg text-gray-900">{displayPeople.address}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Geocoding indicator */}
                        {geocoding && (
                            <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
                                    <p className="text-blue-800">
                                        Géolocalisation de l'adresse en cours...
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Google Map - Using optimisticPeople for instant updates */}
                        {optimisticPeople?.latitude && optimisticPeople?.longitude &&
                         optimisticPeople.latitude !== 'loading' && optimisticPeople.longitude !== 'loading' && (
                            <div className="mt-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Localisation</h3>
                                <div className="rounded-lg overflow-hidden border-2 border-gray-200" style={{ height: '400px' }}>
                                    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                                        <Map
                                            defaultCenter={{
                                                lat: parseFloat(optimisticPeople.latitude),
                                                lng: parseFloat(optimisticPeople.longitude)
                                            }}
                                            defaultZoom={15}
                                            mapId="cabaza-map"
                                        >
                                            <AdvancedMarker
                                                position={{
                                                    lat: parseFloat(optimisticPeople.latitude),
                                                    lng: parseFloat(optimisticPeople.longitude)
                                                }}
                                                title={`${optimisticPeople.firstName} ${optimisticPeople.lastName}`}
                                            />
                                        </Map>
                                    </APIProvider>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="mt-8 pt-6 border-t border-gray-200 flex gap-4">
                            <button
                                onClick={() => navigate('/')}
                                className="flex-1 bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
                            >
                                Retour aux contacts
                            </button>
                            <button
                                onClick={handleDelete}
                                className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                            >
                                <Trash2 className="w-5 h-5" />
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PeopleDetail;
